import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, UserPlus, ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth, AppRole } from '@/contexts/AuthContext';
import { useCreateUser } from '@/hooks/useCreateUser';
import { useNavigate } from 'react-router-dom';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  coadmin: 'Co-Administrador',
  admin_cluster: 'Admin Cluster',
  gestor: 'Gestor',
  gestor_planejamento: 'Gestor Planejamento',
  gestor_qualidade: 'Gestor Qualidade',
  gestor_documentacao: 'Gestor Documentação',
  apontador: 'Apontador',
  executor: 'Executor',
  fiscal: 'Fiscal',
  profissional: 'Fiscal de Campo',
  observador: 'Observador',
};

const ROLE_COLORS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  admin: 'destructive',
  coadmin: 'default',
  admin_cluster: 'default',
  gestor: 'secondary',
  gestor_planejamento: 'secondary',
  gestor_qualidade: 'secondary',
  gestor_documentacao: 'secondary',
  apontador: 'outline',
  executor: 'outline',
  fiscal: 'outline',
  profissional: 'outline',
  observador: 'outline',
};

// Roles that each creator level can assign
const ROLES_FOR_ADMIN: { value: AppRole; label: string }[] = [
  { value: 'admin', label: 'Administrador' },
  { value: 'coadmin', label: 'Co-Administrador' },
  { value: 'admin_cluster', label: 'Admin Cluster' },
  { value: 'gestor_planejamento', label: 'Gestor Planejamento' },
  { value: 'gestor_qualidade', label: 'Gestor Qualidade' },
  { value: 'gestor_documentacao', label: 'Gestor Documentação' },
  { value: 'apontador', label: 'Apontador' },
  { value: 'observador', label: 'Observador' },
];

const ROLES_FOR_ADMIN_CLUSTER: { value: AppRole; label: string }[] = [
  { value: 'gestor_planejamento', label: 'Gestor Planejamento' },
  { value: 'gestor_qualidade', label: 'Gestor Qualidade' },
  { value: 'gestor_documentacao', label: 'Gestor Documentação' },
  { value: 'apontador', label: 'Apontador' },
  { value: 'observador', label: 'Observador' },
];

export const EmpUsuariosView: React.FC = () => {
  const { isAdmin, isCoAdmin, role, user } = useAuth();
  const isAdminCluster = role === 'admin_cluster';
  const canCreateUsers = isAdmin || isCoAdmin || isAdminCluster;

  const { createUser, loading: creating } = useCreateUser();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: '' as string });

  // Fetch caller's cluster_id for admin_cluster auto-fill
  const { data: callerProfile } = useQuery({
    queryKey: ['caller-profile-cluster', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('cluster_id')
        .eq('id', user.id)
        .maybeSingle();
      return data;
    },
    enabled: isAdminCluster && !!user?.id,
  });

  const availableRoles = useMemo(() => {
    if (isAdmin || isCoAdmin) return ROLES_FOR_ADMIN;
    if (isAdminCluster) return ROLES_FOR_ADMIN_CLUSTER;
    return [];
  }, [isAdmin, isCoAdmin, isAdminCluster]);

  const { data: users = [], isLoading, refetch } = useQuery({
    queryKey: ['emp-usuarios-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_users_public')
        .select('user_id, name, email, role')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  const handleCreate = async () => {
    const clusterId = isAdminCluster ? callerProfile?.cluster_id : undefined;
    const success = await createUser({
      email: form.email,
      password: form.password,
      name: form.name,
      role: form.role as AppRole,
      cluster_id: clusterId || null,
    });
    if (success) {
      setShowCreate(false);
      setForm({ email: '', password: '', name: '', role: '' });
      refetch();
    }
  };

  if (isLoading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  const byRole = users.reduce((acc: Record<string, number>, u: any) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Total Usuários</p><p className="text-2xl font-bold">{users.length}</p></CardContent></Card>
          {Object.entries(byRole).slice(0, 3).map(([r, count]) => (
            <Card key={r}><CardContent className="pt-4"><p className="text-xs text-muted-foreground">{ROLE_LABELS[r] || r}</p><p className="text-2xl font-bold">{count as number}</p></CardContent></Card>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Usuários do Sistema</CardTitle>
            <div className="flex gap-2">
              {canCreateUsers && (
                <>
                  {(isAdmin || isCoAdmin) && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/pessoas')} className="gap-2">
                      <ExternalLink className="h-4 w-4" />
                      Gestão Completa
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setShowCreate(true)} className="gap-2">
                    <UserPlus className="h-4 w-4" />
                    Novo Usuário
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">Nenhum usuário encontrado.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Perfil</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u: any) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant={ROLE_COLORS[u.role] || 'outline'}>{ROLE_LABELS[u.role] || u.role}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal criar usuário */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Usuário</DialogTitle>
            <DialogDescription>
              {isAdminCluster
                ? 'Crie um usuário dentro do seu cluster. O cluster será atribuído automaticamente.'
                : 'Crie um acesso ao sistema. Para gestão completa de pessoas, use a página dedicada.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nome</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nome completo" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" />
            </div>
            <div>
              <Label>Senha</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Mínimo 8 caracteres" />
            </div>
            <div>
              <Label>Perfil</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o perfil" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={creating || !form.name || !form.email || !form.password || !form.role}>
              {creating ? 'Criando...' : 'Criar Usuário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
